<script setup lang="ts">
    import { computed } from 'vue';
    import type { Question } from '../../SurveysTypes.d';
    import { questionTypeLabel, questionNumberMinMax, questionMaxOptionsLabel } from '../SurveyStatistics.utils';
    import { formatNumericText } from '@shared/utils/formatNumericText';

    const props = withDefaults(
        defineProps<{
            question: Question;
            answered?: boolean;
            showAverage?: boolean;
        }>(),
        { answered: undefined, showAverage: false }
    );

    const numberMinMax = computed(() => questionNumberMinMax(props.question));
    const maxOptionsLabel = computed(() => questionMaxOptionsLabel(props.question));
</script>

<template>
    <ul class="list-unstyled inline-divider-list fw-600 m-md-t">
        <li>{{ questionTypeLabel(question) }}</li>
        <li v-if="maxOptionsLabel">
            {{ maxOptionsLabel }}
        </li>
        <li v-if="numberMinMax">
            {{ $Resources.QuestionNumberBlockMinLabel }}: {{ numberMinMax.min }}, {{ $Resources.QuestionNumberBlockMaxLabel.toLowerCase() }}:
            {{ numberMinMax.max }}
        </li>
        <li v-if="question.RandomizeQuestionOptionOrder">{{ $Resources.QuestionOptionOrderIsRandomizedLabel }}</li>
        <li v-if="question.Required">* {{ $Resources.QuestionRequiredLabel }}</li>
        <li v-if="showAverage && question.AverageNumericValue !== null">
            {{ $Resources.AverageLabel }}:
            {{ formatNumericText(question.AverageNumericValue, { precision: 2 }) }}
        </li>
        <li v-if="question.IsSensitive">
            <span class="label label-warning text-md">
                {{ $Resources.SensitiveQuestionCanvasLabel }}
            </span>
        </li>
        <li v-if="question.IsInactive">
            <span class="label label-warning text-md">
                {{ $Resources.InactivatedQuestionLabel }}
            </span>
        </li>
        <li v-if="question.IsDeleted">
            <span class="label label-danger text-md">
                {{ $Resources.SurveyDeletedQuestionsLabel }}
            </span>
        </li>
        <li v-if="answered === false">
            <span class="label label-warning text-md">
                {{ $Resources.SurveyResponseNotAnsweredText }}
            </span>
        </li>
    </ul>
</template>

<style>
    .inline-divider-list {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }

    .inline-divider-list li:not(:last-child):not(:has(+ li .label)) {
        border-right: 1px solid var(--border-color);
        padding-right: var(--ug-spacing-sm);
        margin-right: var(--ug-spacing-sm);
    }

    .inline-divider-list li:has(.label) {
        margin-left: auto;
        padding-left: var(--ug-spacing-xs);
    }

    .inline-divider-list li:has(.label) ~ li:has(.label) {
        margin-left: 0;
    }
</style>
